import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import Icon from '../../components/common/Icon';
import Button from '../../components/common/Button';
import RefundDeadlineBadge from '../../components/common/RefundDeadlineBadge';
import { isRefundPending, buildClosedRefundMessage } from '../../utils/refundTimeline';
import { useAuth } from '../../context/AuthContext';
import { orderAPI, adminAPI, notificationAPI } from '../../services/api';
import PaginationBar from '../../components/common/PaginationBar';

const ORDER_STATUS = {
  ORDER_FORM_FILLED: 'ORDER_FORM_FILLED',
  REFUND_FORM_FILLED: 'REFUND_FORM_FILLED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  REJECT_AND_REFILL: 'REJECT_AND_REFILL',
  ORDER_FORM_REFILL_REQUEST: 'ORDER_FORM_REFILL_REQUEST',
  REFUND_FORM_REFILL_REQUEST: 'REFUND_FORM_REFILL_REQUEST',
  PAID: 'PAID',
  PAID_TO_MEDIATOR: 'PAID_TO_MEDIATOR',
  PAID_TO_BUYER: 'PAID_TO_BUYER'
};

const formatOrderTitle = (order) => {
  const brand = order.brandName?.trim();
  const product = order.productName?.trim() || 'Product';
  return brand ? `${brand}: ${product}` : product;
};

const getDisplayStatus = (status) => {
  // PAID and PAID_TO_MEDIATOR should be displayed as 'Approved'
  if ([ORDER_STATUS.APPROVED, ORDER_STATUS.PAID, ORDER_STATUS.PAID_TO_MEDIATOR].includes(status)) 
    return { label: 'Approved', color: '#10b981', bg: '#d1fae5' };
  
  // Only PAID_TO_BUYER should be shown as 'Paid'
  if (status === ORDER_STATUS.PAID_TO_BUYER) 
    return { label: 'Paid', color: '#059669', bg: '#ecfdf5' };
    
  if (status === ORDER_STATUS.REJECTED) 
    return { label: 'Rejected', color: '#ef4444', bg: '#fee2e2' };
    
  if ([ORDER_STATUS.ORDER_FORM_REFILL_REQUEST, ORDER_STATUS.REFUND_FORM_REFILL_REQUEST, ORDER_STATUS.REJECT_AND_REFILL].includes(status))
    return { label: status === ORDER_STATUS.REJECT_AND_REFILL ? 'Reject and Refill' : 'Refill Required', color: '#8b5cf6', bg: '#f3e8ff' };
    
  return { label: status?.replace(/_/g, ' ') || 'Pending', color: '#64748b', bg: '#f1f5f9' };
};

const getProgressIndex = (status) => {
  switch (status) {
    case ORDER_STATUS.ORDER_FORM_FILLED:
      return 0;
    case ORDER_STATUS.REFUND_FORM_FILLED:
    case ORDER_STATUS.ORDER_FORM_REFILL_REQUEST:
    case ORDER_STATUS.REFUND_FORM_REFILL_REQUEST:
    case ORDER_STATUS.REJECT_AND_REFILL:
      return 1;
    case ORDER_STATUS.APPROVED:
    case ORDER_STATUS.PAID:
    case ORDER_STATUS.PAID_TO_MEDIATOR:
      return 2;
    case ORDER_STATUS.PAID_TO_BUYER:
      return 3;
    case ORDER_STATUS.REJECTED:
    case ORDER_STATUS.CANCELLED:
      return -1;
    default:
      return 0;
  }
};

const getStatusBadge = (status) => {
  switch (status) {
    case ORDER_STATUS.ORDER_FORM_FILLED:
      return { label: 'Ordered', bg: '#E0E7FF', color: '#4F46E5' };
    case ORDER_STATUS.REFUND_FORM_FILLED:
      return { label: 'Refund Filed', bg: '#DCFCE7', color: '#15803D' };
    case ORDER_STATUS.APPROVED:
      return { label: 'Approved', bg: '#DCFCE7', color: '#15803D' };
    case ORDER_STATUS.PAID:
      return { label: 'Paid', bg: '#DCFCE7', color: '#15803D' };
    case ORDER_STATUS.REJECTED:
      return { label: 'Rejected', bg: '#FEE2E2', color: '#DC2626' };
    case ORDER_STATUS.CANCELLED:
      return { label: 'Cancelled', bg: '#F3F4F6', color: '#6B7280' };
    case ORDER_STATUS.REFILL_REQUEST:
      return { label: 'Refill Request', bg: '#FEF3C7', color: '#D97706' };
    case ORDER_STATUS.ORDER_FORM_REFILL_REQUEST:
      return { label: 'Order Refill', bg: '#F5F3FF', color: '#5B21B6' };
    case ORDER_STATUS.REFUND_FORM_REFILL_REQUEST:
    case ORDER_STATUS.REJECT_AND_REFILL:
      return { label: status === ORDER_STATUS.REJECT_AND_REFILL ? 'Reject and Refill' : 'Refund Refill', bg: '#FCE7F3', color: '#BE185D' };
    default:
      return { label: status, bg: '#F3F4F6', color: '#6B7280' };
  }
};

const formatCurrency = (amount) => `₹${amount}`;

const progressSteps = [
  { key: 'ORDER_FORM_FILLED', label: 'ORDERED' },
  { key: 'REFUND_FORM_FILLED', label: 'REFUND FILED' },
  { key: 'APPROVED', label: 'APPROVED' },
  { key: 'PAID_TO_BUYER', label: 'PAID' },
];

const renderProgress = (status) => {
  const currentIndex = getProgressIndex(status);
  const isRejected = status === ORDER_STATUS.REJECTED;
  const isCancelled = status === ORDER_STATUS.CANCELLED;
  const isRefill = [ORDER_STATUS.ORDER_FORM_REFILL_REQUEST, ORDER_STATUS.REFUND_FORM_REFILL_REQUEST, ORDER_STATUS.REJECT_AND_REFILL].includes(status);

  if (isRejected || isCancelled || isRefill) {
    return (
      <div style={{ marginTop: '24px' }}>
        <div
          style={{
            padding: '14px 18px',
            borderRadius: '14px',
            background: isRejected ? '#FEE2E2' : isRefill ? '#F5F3FF' : '#F3F4F6',
            color: isRejected ? '#B91C1C' : isRefill ? '#5B21B6' : '#4B5563',
            fontWeight: 600,
            fontSize: '14px',
            border: `1px solid ${isRejected ? '#FECACA' : isRefill ? '#DDD6FE' : '#D1D5DB'}`,
          }}
        >
          {isRejected ? 'This order has been rejected.' : 
           isRefill ? 'Action Required: Please refill the form with the requested changes.' :
           'This order has been cancelled.'}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: '12px',
        alignItems: 'start',
        marginTop: '24px',
      }}
    >
      {progressSteps.map((step, index) => {
        const completed = index < currentIndex;
        const active = index === currentIndex;
        const pending = index > currentIndex;

        return (
          <div key={step.key} style={{ textAlign: 'center', position: 'relative' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '10px',
                position: 'relative',
              }}
            >
              {index !== 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: '-50%',
                    top: '50%',
                    width: '100%',
                    height: '4px',
                    transform: 'translateY(-50%)',
                    background:
                      index <= currentIndex ? '#16A34A' : '#D1D5DB',
                    zIndex: 0,
                  }}
                />
              )}

              <div
                style={{
                  width: '52px',
                  height: '52px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '18px',
                  position: 'relative',
                  zIndex: 1,
                  background: completed || active ? '#16A34A' : '#F3F4F6',
                  color: completed || active ? '#FFFFFF' : '#9CA3AF',
                  boxShadow: active ? '0 0 0 8px rgba(34,197,94,0.15)' : 'none',
                }}
              >
                {completed ? '✓' : index + 1}
              </div>
            </div>

            <div
              style={{
                fontSize: '13px',
                fontWeight: 700,
                color: completed || active ? '#15803D' : '#9CA3AF',
                letterSpacing: '0.4px',
              }}
            >
              {step.label}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const BuyerDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [apiNotifications, setApiNotifications] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10
  });
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingPayments: 0,
    refillRequests: 0,
    rejectedOrders: 0,
    pendingRefundForms: 0,
    overdueRefundForms: 0,
    urgentRefundForms: 0,
    closedRefundForms: 0,
    closingSoonRefundForms: 0,
  });

  const calculateDeadlines = (orderList) => {
    const newNotifications = [];

    orderList.forEach(order => {
      const timeline = order.refundTimeline;
      if (timeline?.refundRequired) {
        if (!timeline.refundFormOpen) {
          newNotifications.push({
            id: `closed-${order.id}`,
            type: 'danger',
            message: `Refund form closed for order ${order.orderId}. ${buildClosedRefundMessage(timeline.policy).replace(/^Refund form is closed\. /, '')}`,
            orderId: order.orderId,
          });
        } else if (['PENALTY_50', 'PENALTY_100'].includes(timeline.warningLevel)) {
          newNotifications.push({
            id: `penalty-${order.id}`,
            type: 'danger',
            message: timeline.warningMessage || `Fill refund for ${order.orderId} now. Penalty ₹${timeline.penaltyAmount} applies.`,
            orderId: order.orderId,
            action: 'refund',
          });
        } else if (timeline.warningLevel === 'REMINDER') {
          newNotifications.push({
            id: `deadline-${order.id}`,
            type: 'warning',
            message: timeline.warningMessage || `Please fill refund form for order ${order.orderId}. ${timeline.daysLeftToClose} days left.`,
            orderId: order.orderId,
            action: 'refund',
          });
        } else if (timeline.daysLeftToClose != null && timeline.daysLeftToClose > 0 && timeline.daysLeftToClose <= 5) {
          newNotifications.push({
            id: `closing-soon-${order.id}`,
            type: 'danger',
            message: `Urgent: Only ${timeline.daysLeftToClose} day(s) left to fill refund form for order ${order.orderId}.`,
            orderId: order.orderId,
            action: 'refund',
          });
        }
      }

      if (order.orderStatus === ORDER_STATUS.REJECTED) {
        newNotifications.push({
          id: `rejected-${order.id}`,
          type: 'danger',
          message: `Order ${order.orderId} was rejected. Please check the reason and contact support.`,
          orderId: order.orderId,
        });
      }

      if ([ORDER_STATUS.ORDER_FORM_REFILL_REQUEST, ORDER_STATUS.REFUND_FORM_REFILL_REQUEST, ORDER_STATUS.REJECT_AND_REFILL].includes(order.orderStatus)) {
        newNotifications.push({
          id: `refill-${order.id}`,
          type: 'info',
          message: order.orderStatus === ORDER_STATUS.REJECT_AND_REFILL
            ? `Order ${order.orderId} was rejected. Please refill the refund form.${order.reason ? ` Reason: ${order.reason}` : ''}`
            : `Refill requested for order ${order.orderId}. Please fill the form again.${order.reason ? ` Reason: ${order.reason}` : ''}`,
          orderId: order.orderId,
          action: order.orderStatus === ORDER_STATUS.ORDER_FORM_REFILL_REQUEST ? 'order' : 'refund',
          orderDbId: order.id,
        });
      }
    });

    setNotifications(newNotifications);
  };

  // Get user email from various sources
  const getUserEmail = () => {
    // First try localStorage (most reliable based on OrderForm findings)
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        if (parsedUser.email) {
          return parsedUser.email;
        }
      } catch (e) {
        // Silently handle parsing error
      }
    }
    
    // Then try user.email from AuthContext
    if (user?.email) {
      return user.email;
    }
    
    return null;
  };

  // Fetch orders from API
  const fetchOrders = async (page = 1, search = '') => {
    // Try localStorage if context email is not ready
    const userEmail = user?.email || JSON.parse(localStorage.getItem('user') || '{}')?.email;
    if (!userEmail) {
      console.log('Dashboard: No user email found for fetching orders');
      return;
    }
    
    setLoading(true);
    try {
      const params = {
        page: page - 1,
        size: pagination.pageSize,
        userEmail: userEmail,
        orderId: search.trim() || undefined
      };
      
      console.log('Dashboard: Fetching orders with params:', params);
      const response = await orderAPI.searchOrders(params);
      console.log('Dashboard: Raw Orders API response (search):', response);
      
      const data = response.data;
      console.log('Dashboard: response.data (search):', data);
      
      // Handle orders data mapping - looking for data.data as per provided JSON
      const ordersRaw = response.data;
      let ordersArray = [];
      
      if (ordersRaw && Array.isArray(ordersRaw.data)) {
        ordersArray = ordersRaw.data;
      } else if (Array.isArray(ordersRaw)) {
        ordersArray = ordersRaw;
      } else if (ordersRaw && Array.isArray(ordersRaw.content)) {
        ordersArray = ordersRaw.content;
      }

      console.log('Dashboard: Extracted Orders Array (search):', ordersArray);

      const fetchedOrders = ordersArray.map(order => ({
        id: order.id,
        orderId: order.orderId,
        productName: order.productName,
        brandName: order.brandName || '',
        date: order.orderDate,
        orderDate: order.orderDate,
        refundFormFilledDate: order.refundFormFilledDate,
        amount: order.orderAmount,
        status: order.orderStatus, // Map orderStatus from API to status
        orderStatus: order.orderStatus,
        reason: order.reason,
        refundTimeline: order.refundTimeline,
      }));

      setOrders(fetchedOrders);
      calculateDeadlines(fetchedOrders);
      
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        totalPages: data.totalPages || 1,
        totalItems: data.totalElements || fetchedOrders.length
      }));
    } catch (error) {
      console.error('Dashboard: Failed to fetch orders:', error);
      setOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const userEmail = getUserEmail();
      
      if (!userEmail) {
        console.log('Dashboard: No user email found for dashboard stats');
        return;
      }

      console.log('Dashboard: Fetching data for email:', userEmail);
      
      const [statsRes, ordersRes] = await Promise.all([
        adminAPI.getBuyerStats(userEmail),
        orderAPI.searchOrders({ 
          userEmail, 
          page: 0,
          size: 10, 
          sortBy: 'creationDate', 
          sortDirection: 'DESC' 
        })
      ]);

      console.log('Dashboard: Stats API Raw Response:', statsRes);
      console.log('Dashboard: Orders API Raw Response:', ordersRes);

      // Extract and set stats
      if (statsRes.data) {
        const statsData = statsRes.data.data || statsRes.data;
        console.log('Dashboard: Final Stats to set:', statsData);
        setStats({
          totalOrders: statsData.totalOrders || 0,
          pendingPayments: statsData.pendingPayments || 0,
          refillRequests: statsData.refillRequests || 0,
          rejectedOrders: statsData.rejectedOrders || 0,
          pendingRefundForms: statsData.pendingRefundForms || 0,
          overdueRefundForms: statsData.overdueRefundForms || 0,
          urgentRefundForms: statsData.urgentRefundForms || 0,
          closedRefundForms: statsData.closedRefundForms || 0,
          closingSoonRefundForms: statsData.closingSoonRefundForms || 0,
        });
      }

      // Handle orders data mapping
      const ordersRaw = ordersRes.data;
      console.log('Dashboard: Raw ordersRes.data:', ordersRaw);
      
      let ordersArray = [];
      if (ordersRaw && Array.isArray(ordersRaw.data)) {
        ordersArray = ordersRaw.data;
      } else if (Array.isArray(ordersRaw)) {
        ordersArray = ordersRaw;
      } else if (ordersRaw && Array.isArray(ordersRaw.content)) {
        ordersArray = ordersRaw.content;
      }

      console.log('Dashboard: Extracted ordersArray:', ordersArray);

      const mappedOrders = ordersArray.map(order => ({
        id: order.id,
        orderId: order.orderId,
        productName: order.productName,
        brandName: order.brandName || '',
        date: order.orderDate,
        orderDate: order.orderDate,
        refundFormFilledDate: order.refundFormFilledDate,
        amount: order.orderAmount,
        status: order.orderStatus,
        orderStatus: order.orderStatus,
        reason: order.reason,
        refundTimeline: order.refundTimeline,
      }));
      
      console.log('Dashboard: Mapped Orders array:', mappedOrders);
      setOrders(mappedOrders);
      calculateDeadlines(mappedOrders);

      try {
        const notifRes = await notificationAPI.getAll();
        const list = notifRes.data?.data || notifRes.data || [];
        setApiNotifications(Array.isArray(list) ? list.filter((n) => !n.readStatus).slice(0, 5) : []);
      } catch {
        setApiNotifications([]);
      }
      
      // Update pagination
      if (ordersRaw && typeof ordersRaw === 'object') {
        setPagination(prev => ({
          ...prev,
          totalPages: ordersRaw.totalPages || 1,
          totalItems: ordersRaw.totalElements || mappedOrders.length
        }));
      } else {
        setPagination(prev => ({
          ...prev,
          totalPages: 1,
          totalItems: mappedOrders.length
        }));
      }
    } catch (error) {
      console.error('Dashboard: Critical error in fetchDashboardData:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [user]);

  // Handle search
  const handleSearch = (value) => {
    setSearchTerm(value);
    fetchOrders(1, value);
  };

  // Handle pagination
  const handlePageChange = (page) => {
    fetchOrders(page, searchTerm);
  };
  
  const totalOrders = stats.totalOrders;
  const refillRequests = stats.refillRequests || 0; // Backend should provide this or we calculate from paid orders
  const pending = stats.pendingPayments;
  const rejected = stats.rejectedOrders;

  return (
    <div title="Dashboard">
      <div style={{ maxWidth: '1240px', margin: '0 auto', padding: '32px 24px' }}>
        {/* Notifications Section */}
        {(apiNotifications.length > 0 || notifications.length > 0) && (
          <div style={{ marginBottom: '24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {apiNotifications.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '14px', fontWeight: 700, color: '#1e293b' }}>Recent alerts</span>
                  <button
                    type="button"
                    onClick={() => navigate('/buyer/notifications')}
                    style={{ background: 'none', border: 'none', color: '#667eea', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                  >
                    View all
                  </button>
                </div>
                {apiNotifications.map((notif) => (
                  <div
                    key={notif.id}
                    style={{
                      padding: '14px 18px',
                      borderRadius: '12px',
                      background: '#f8faff',
                      border: '1px solid #c7d2fe',
                      fontSize: '14px',
                      color: '#334155',
                    }}
                  >
                    <strong>{notif.title}</strong>
                    <div style={{ marginTop: '4px', fontSize: '13px', color: '#64748b' }}>{notif.message}</div>
                  </div>
                ))}
              </div>
            )}
            {stats.pendingRefundForms > 0 && (
              <div style={{
                padding: '16px 20px', borderRadius: '14px', background: '#eff6ff',
                border: '1px solid #bfdbfe', fontSize: '16px', fontWeight: 700, color: '#1d4ed8'
              }}>
                {stats.pendingRefundForms} refund form(s) pending
                {stats.closingSoonRefundForms > 0 && ` · ${stats.closingSoonRefundForms} closing within 5 days`}
                {stats.urgentRefundForms > 0 && ` · ${stats.urgentRefundForms} urgent (penalty applies)`}
                {stats.overdueRefundForms > 0 && ` · ${stats.overdueRefundForms} overdue (15+ days)`}
              </div>
            )}
            {notifications.map((notif) => (
              <div
                key={notif.id}
                style={{
                  padding: '16px 20px',
                  borderRadius: '12px',
                  background: notif.type === 'danger' ? '#FEF2F2' : notif.type === 'warning' ? '#FFFBEB' : '#EFF6FF',
                  border: `1px solid ${notif.type === 'danger' ? '#FECACA' : notif.type === 'warning' ? '#FEF3C7' : '#DBEAFE'}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  animation: 'slideIn 0.3s ease-out'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Icon 
                    name={notif.type === 'danger' ? 'alert-circle' : 'alert-triangle'} 
                    size={20} 
                    style={{ color: notif.type === 'danger' ? '#DC2626' : notif.type === 'warning' ? '#D97706' : '#2563EB' }} 
                  />
                  <span style={{ 
                    fontSize: '14px', 
                    fontWeight: '600', 
                    color: notif.type === 'danger' ? '#991B1B' : notif.type === 'warning' ? '#92400E' : '#1E40AF' 
                  }}>
                    {notif.message}
                  </span>
                </div>
                {notif.action && (
                  <Button
                    size="sm"
                    variant={notif.type === 'danger' ? 'danger' : 'primary'}
                    onClick={() => navigate(
                      notif.action === 'refund'
                        ? `/refund?orderId=${encodeURIComponent(notif.orderId || '')}`
                        : '/order',
                      { state: { orderId: notif.orderId } }
                    )}
                  >
                    {notif.action === 'refund' ? 'Fill Refund Form' : 'Fill Form'}
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: '24px',
            marginBottom: '32px',
          }}
        >
          {[
            { label: 'TOTAL ORDERS', value: totalOrders, topColor: '#6366F1' },
            { label: 'Refill Request', value: refillRequests, topColor: '#16A34A' },
            { label: 'PENDING', value: pending, topColor: '#EA580C' },
            { label: 'REJECTED', value: rejected, topColor: '#DC2626' },
          ].map((item) => (
            <div key={item.label}>
              <div
                style={{
                  height: '4px',
                  background: item.topColor,
                  borderRadius: '999px',
                  marginBottom: '8px',
                }}
              />
              <div
                style={{
                  fontSize: '14px',
                  fontWeight: 700,
                  color: '#9CA3AF',
                  marginBottom: '12px',
                  letterSpacing: '0.6px',
                }}
              >
                {item.label}
              </div>
              <div
                style={{
                  fontSize: '44px',
                  fontWeight: 800,
                  color: '#0F172A',
                  lineHeight: 1.1,
                }}
              >
                {item.value}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '36px',
          }}
        >
          <div
            style={{
              borderRadius: '30px',
              padding: '34px 36px',
              color: 'white',
              background: 'linear-gradient(135deg, #5B5CEB 0%, #4F46E5 100%)',
              boxShadow: '0 12px 24px rgba(79,70,229,0.18)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onClick={() => navigate('/order')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 16px 32px rgba(79,70,229,0.24)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(79,70,229,0.18)';
            }}
          >
            <div style={{ fontSize: '22px', fontWeight: 800, marginBottom: '8px' }}>
              Submit New Order
            </div>
            <div style={{ fontSize: '16px', opacity: 0.9 }}>
              Track your purchase
            </div>
          </div>

          <div
            style={{
              borderRadius: '30px',
              padding: '34px 36px',
              color: 'white',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              boxShadow: '0 12px 24px rgba(16,185,129,0.18)',
              cursor: 'pointer',
              transition: 'transform 0.2s, box-shadow 0.2s'
            }}
            onClick={() => window.location.href = `mailto:support@zmaonline.com?subject=Support Request - ${user?.email || 'Buyer'}&body=Hello ZMA Support team,%0D%0A%0D%0AMy Name: ${user?.name || ''}%0D%0AMy Email: ${user?.email || ''}%0D%0A%0D%0AIssue Description:%0D%0A`}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 16px 32px rgba(16,185,129,0.24)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 12px 24px rgba(16,185,129,0.18)';
            }}
          >
            <div style={{ fontSize: '22px', fontWeight: 800, marginBottom: '8px' }}>
              Contact Us
            </div>
            <div style={{ fontSize: '16px', opacity: 0.9 }}>
              Get help from support
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '20px',
          }}
        >
          <h2
            style={{
              fontSize: '28px',
              fontWeight: 800,
              color: '#0F172A',
              margin: 0,
            }}
          >
            My Orders
          </h2>

          <button
            style={{
              background: '#FFFFFF',
              border: '1px solid #D1D5DB',
              borderRadius: '18px',
              padding: '14px 22px',
              fontSize: '16px',
              fontWeight: '600',
              color: '#475569',
              boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
              cursor: 'pointer',
            }}
          >
            Export
          </button>
        </div>

        {/* Search Field */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ position: 'relative' }}>
            <input
              type="text"
              placeholder="Search by Order ID, Product Code, or Product Name..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              style={{
                width: '100%',
                padding: '16px 20px 16px 52px',
                border: '1px solid #D1D5DB',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '500',
                color: '#0F172A',
                background: '#FFFFFF',
                boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
                outline: 'none',
                transition: 'border-color 0.2s, box-shadow 0.2s'
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#4F46E5';
                e.target.style.boxShadow = '0 0 0 3px rgba(79,70,229,0.1)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#D1D5DB';
                e.target.style.boxShadow = '0 4px 12px rgba(15,23,42,0.08)';
              }}
            />
            <div
              style={{
                position: 'absolute',
                left: '20px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9CA3AF',
                pointerEvents: 'none'
              }}
            >
              <Icon name="search" size={20} />
            </div>
            {searchTerm && (
              <button
                onClick={() => handleSearch('')}
                style={{
                  position: 'absolute',
                  right: '16px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: '#9CA3AF',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <Icon name="x" size={16} />
              </button>
            )}
          </div>
          {searchTerm && (
            <p style={{ 
              margin: '8px 0 0 0', 
              fontSize: '14px', 
              color: '#6B7280' 
            }}>
              Showing results for "{searchTerm}" ({pagination.totalItems} found)
            </p>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '22px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ 
                display: 'inline-block', 
                width: '40px', 
                height: '40px', 
                border: '3px solid #f3f3f3', 
                borderTop: '3px solid #4F46E5', 
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ marginTop: '16px', color: '#6B7280', fontSize: '16px' }}>
                Loading orders...
              </p>
            </div>
          ) : orders.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px',
              background: '#FFFFFF',
              border: '1px solid #D9DEE7',
              borderRadius: '16px'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>📦</div>
              <h3 style={{ 
                fontSize: '18px', 
                fontWeight: '600', 
                color: '#374151',
                margin: '0 0 8px 0'
              }}>
                {searchTerm ? 'No orders found' : 'No orders yet'}
              </h3>
              <p style={{ 
                fontSize: '14px', 
                color: '#6B7280',
                margin: '0 0 24px 0'
              }}>
                {searchTerm 
                  ? `No orders match "${searchTerm}". Try a different search term.`
                  : 'Start by submitting your first order.'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => navigate('/order')}
                  style={{
                    padding: '12px 24px',
                    background: '#4F46E5',
                    color: 'white',
                    border: 'none',
                    borderRadius: '12px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Submit Order
                </button>
              )}
            </div>
          ) : (
            orders.map((order) => {
              const badge = getDisplayStatus(order.status);
              const isRefillOrder = order.status === ORDER_STATUS.ORDER_FORM_REFILL_REQUEST;
              const isRefillRefund = order.status === ORDER_STATUS.REFUND_FORM_REFILL_REQUEST
                || order.status === ORDER_STATUS.REJECT_AND_REFILL;

              return (
                <div
                  key={order.id}
                  style={{
                    background: '#FFFFFF',
                    border: '1px solid #D9DEE7',
                    borderRadius: '30px',
                    padding: '28px 32px',
                    boxShadow: '0 4px 12px rgba(15,23,42,0.06)',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      gap: '20px',
                    }}
                  >
                    <div>
                      <h3
                        style={{
                          margin: '0 0 8px 0',
                          fontSize: '20px',
                          fontWeight: 800,
                          color: '#0F172A',
                        }}
                      >
                        {formatOrderTitle(order)}
                      </h3>

                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <span
                          style={{
                            fontSize: '18px',
                            fontWeight: 600,
                            color: '#4F46E5',
                            background: '#EEF2FF',
                            padding: '4px 12px',
                            borderRadius: '8px'
                          }}
                        >
                          {order.orderId}
                        </span>

                        <span
                          style={{
                            background: badge.bg,
                            color: badge.color,
                            borderRadius: '999px',
                            padding: '8px 14px',
                            fontSize: '14px',
                            fontWeight: 700,
                          }}
                        >
                          {badge.label}
                        </span>

                        <span
                          style={{
                            fontSize: '16px',
                            color: '#9CA3AF',
                          }}
                        >
                          {order.date}
                        </span>
                        {order.refundFormFilledDate && (
                          <span
                            style={{
                              fontSize: '14px',
                              color: '#64748b',
                            }}
                          >
                            Refund filled: {order.refundFormFilledDate}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
                      <div
                        style={{
                          fontSize: '28px',
                          fontWeight: 800,
                          color: '#15803D',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {formatCurrency(order.amount)}
                      </div>
                      
                      {(isRefillOrder || isRefillRefund) && (
                        <Button
                          size="sm"
                          onClick={() => navigate(
                            isRefillOrder
                              ? `/order?edit=${order.id}&refill=true`
                              : `/refund?edit=${order.id}&refill=true&orderId=${encodeURIComponent(order.orderId)}`,
                            {
                              state: {
                                orderId: order.orderId,
                                isEdit: true,
                                orderData: order,
                              },
                            }
                          )}
                          style={{ 
                            background: '#8b5cf6', 
                            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.25)' 
                          }}
                        >
                          <Icon name="edit" size={14} style={{ marginRight: '6px' }} />
                          {isRefillOrder ? 'Refill Order Form' : 'Refill Refund Form'}
                        </Button>
                      )}

                      {isRefundPending(order.status) && order.refundTimeline?.refundFormOpen && !isRefillOrder && !isRefillRefund && (
                        <Button
                          size="sm"
                          onClick={() => navigate(`/refund?orderId=${encodeURIComponent(order.orderId)}`, {
                            state: { orderId: order.orderId, orderData: order },
                          })}
                          style={{
                            background: '#4F46E5',
                            boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
                          }}
                        >
                          Fill Refund Form
                        </Button>
                      )}
                    </div>
                  </div>

                  {order.reason && (isRefillOrder || isRefillRefund || order.status === ORDER_STATUS.REJECTED) && (
                    <div style={{ 
                      marginTop: '16px', 
                      padding: '12px 16px', 
                      background: order.status === ORDER_STATUS.REJECTED ? '#FEF2F2' : '#F5F3FF', 
                      borderRadius: '12px',
                      border: `1px solid ${order.status === ORDER_STATUS.REJECTED ? '#FECACA' : '#DDD6FE'}`,
                      fontSize: '14px'
                    }}>
                      <span style={{ fontWeight: 700, color: order.status === ORDER_STATUS.REJECTED ? '#B91C1C' : '#5B21B6' }}>Reason: </span>
                      <span style={{ color: order.status === ORDER_STATUS.REJECTED ? '#991B1B' : '#4C1D95' }}>{order.reason}</span>
                    </div>
                  )}

                  {renderProgress(order.status)}

                  {isRefundPending(order.status) && order.refundTimeline && (
                    <div style={{ marginTop: '16px' }}>
                      <RefundDeadlineBadge timeline={order.refundTimeline} large />
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Pagination */}
      {!loading && orders.length > 0 && pagination.totalPages > 1 && (
        <PaginationBar
          page={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalItems}
          pageSize={pagination.pageSize || 10}
          pageBase={1}
          onPageChange={handlePageChange}
          style={{ borderTop: '1px solid #D9DEE7', marginTop: '16px', padding: '16px 0', justifyContent: 'center' }}
        />
      )}
    </div>
  );
};

export default BuyerDashboard;
